import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useCallback, useEffect, useState } from "react";
import { PageContainer, PageHeader } from "../components/PageHeader";
import { friendlyError } from "../utils/apiError";
import { slugifyId, CATALOGUE_ID_RE } from "../utils/slug";
import PackageChannelPicker from "../components/PackageChannelPicker";
import PackageOverlapNotice from "../components/PackageOverlapNotice";
import {
  foyerApi,
  type ContentPackagePreview,
  type ContentPackageRow,
  type ContentSupplierRow,
  type PackageOverlapReport,
} from "../http/foyer";
import type { PackageRule } from "@shared/content-package";
import { tokens } from "../theme";

/**
 * Content packages (`BE-30`) — what a room is entitled to, expressed as a RULE
 * over trusted suppliers rather than as a list somebody maintains.
 *
 * Read `docs/content-packages.md`. Three things this screen exists to make
 * true, each of which is a decision rather than a layout:
 *
 * **1. Trust is shown beside the packages it feeds, not on a settings page.**
 * Flipping a supplier to trusted is what makes their future titles reach guests
 * unattended. It belongs next to the consequence.
 *
 * **2. A rule is never saved blind.** Every edit shows the diff against the
 * pinned ids FIRST — what the rule adds, what it does not cover, which it makes
 * redundant. Nobody should be asked to trade a list they can see for a rule
 * they cannot, and `pinsOnly` is also the first time the drift `BE-18` found
 * (pinned ids for content that no longer exists) is visible anywhere.
 *
 * **3. A capped count says so.** `resolved.truncated` means the number is a
 * prefix. A silent cap reads as "this is the whole package", which is exactly
 * the class of lie the ticket exists to remove.
 */

const EMPTY_RULE: PackageRule = { suppliers: [] };

/** Comma-separated in, cleaned list out. The rule fields are all id lists. */
function parseList(value: string): string[] {
  return value
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
}

export default function ContentPackagesPage() {
  const [packages, setPackages] = useState<ContentPackageRow[]>([]);
  const [suppliers, setSuppliers] = useState<ContentSupplierRow[]>([]);
  const [catalogueReachable, setCatalogueReachable] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<ContentPackageRow | null>(null);
  const [picking, setPicking] = useState<ContentPackageRow | null>(null);
  const [creating, setCreating] = useState(false);
  /**
   * `ENT-3`: a near-copy warning from the last save, held at page level.
   *
   * Both dialogs close on save, so a warning rendered inside one would flash
   * and vanish — and a guard nobody sees is the `importFeatureSets()` mistake
   * again. It persists here until dismissed.
   */
  const [savedOverlap, setSavedOverlap] = useState<PackageOverlapReport | null>(
    null
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pkgs, sups] = await Promise.all([
        foyerApi.listContentPackages(),
        foyerApi.listContentSuppliers(),
      ]);
      setPackages(pkgs.packages);
      setSuppliers(sups.suppliers);
      setCatalogueReachable(sups.catalogueReachable);
      setError(null);
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <PageContainer>
      <PageHeader
        eyebrow="Distribution"
        title="Packages"
        subtitle="A package is a bag of content. What a room may watch is decided here, and nowhere else."
      />

      {/*
        The vocabulary on this page is the thing people get stuck on, and until
        now it existed only in the code. Four words, in the order the decision
        is actually made — which is also the order that explains why "excluded"
        beats "curated" beats "by rule".
      */}
      <Alert severity="info" icon={false} sx={{ mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          How a package decides what is in it
        </Typography>
        <Typography variant="body2" component="div">
          <strong>Curated</strong> — titles named one by one. A hand-picked
          list, and it never changes on its own.
          <br />
          <strong>By rule</strong> — titles that match a description, e.g.{" "}
          <em>live channels from SVT</em>. A new SVT channel joins the package
          the day it appears, with nobody editing anything.
          <br />
          <strong>Excluded</strong> — everything the rule catches,{" "}
          <em>except</em> these. Only meaningful when there is a rule.
          <br />
          <strong>Curated only</strong> — this package has a list and no rule.
          It will not pick up anything new by itself.
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          component="div"
          sx={{ mt: 1.5 }}
        >
          A rule matches on the title&apos;s content provider, so a title whose
          provider is unknown is carried by no rule, ever. That is deliberate —
          it fails closed rather than granting something nobody licensed.
        </Typography>
      </Alert>

      {error ? (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      ) : null}

      {savedOverlap ? (
        <Box sx={{ mb: 3 }}>
          <PackageOverlapNotice
            report={savedOverlap}
            onDismiss={() => setSavedOverlap(null)}
          />
        </Box>
      ) : null}

      {!catalogueReachable ? (
        // A rule is evaluated against the catalogue, so an unreachable one
        // means the counts below are unknown rather than zero. Saying which
        // half is missing beats a number that reads as an answer.
        <Alert severity="warning" sx={{ mb: 3 }}>
          The content catalogue is unreachable, so rule counts cannot be worked
          out. Pinned ids and trust decisions are unaffected.
        </Alert>
      ) : null}

      {loading ? (
        <Stack alignItems="center" sx={{ py: 6 }}>
          <CircularProgress />
        </Stack>
      ) : (
        <Stack spacing={4}>
          <PackagesCard
            packages={packages}
            onEdit={setEditing}
            onPickChannels={setPicking}
            onCreate={() => setCreating(true)}
          />
        </Stack>
      )}

      {editing ? (
        <PackageRuleDialog
          // Remounted by key rather than syncing props: a dialog that watches
          // props cannot tell "different record" from "same record reopened".
          key={editing.id}
          pkg={editing}
          suppliers={suppliers}
          onClose={() => setEditing(null)}
          onSaved={async (overlap) => {
            setEditing(null);
            setSavedOverlap(overlap);
            await load();
          }}
        />
      ) : null}

      {picking ? (
        <PackageChannelPicker
          pkg={picking}
          onClose={() => setPicking(null)}
          onSaved={async (overlap) => {
            setPicking(null);
            setSavedOverlap(overlap);
            await load();
          }}
        />
      ) : null}

      {creating ? (
        <CreatePackageDialog
          onClose={() => setCreating(false)}
          onCreated={async () => {
            setCreating(false);
            await load();
          }}
        />
      ) : null}
    </PageContainer>
  );
}

/* -------------------------------------------------------------------------- */

function PackagesCard({
  packages,
  onEdit,
  onPickChannels,
  onCreate,
}: {
  packages: ContentPackageRow[];
  onEdit: (pkg: ContentPackageRow) => void;
  onPickChannels: (pkg: ContentPackageRow) => void;
  onCreate: () => void;
}) {
  return (
    <Card variant="outlined" sx={{ p: 3 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h6">Packages</Typography>
          <Typography variant="body2" color="text.secondary">
            A named set of content — the channels and titles a product grants.
            Put things in by picking them, or by a rule over a content provider.
          </Typography>
        </Box>
        <Button size="small" startIcon={<AddIcon />} onClick={onCreate}>
          New package
        </Button>
      </Stack>

      {packages.length === 0 ? (
        <Alert severity="info">
          No packages yet. Until one exists, an ingested title is reachable by
          nobody. Create one, then choose its channels or give it a rule over a
          trusted content provider.
        </Alert>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Package</TableCell>
                <TableCell>Rule</TableCell>
                <TableCell align="right">By rule</TableCell>
                <TableCell align="right">Curated</TableCell>
                <TableCell align="right">Excluded</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {packages.map((pkg) => (
                <TableRow key={pkg.id} hover>
                  <TableCell>
                    <Typography variant="body2">{pkg.name}</Typography>
                    <Typography
                      variant="caption"
                      sx={{ fontFamily: "monospace", color: tokens.inkMuted }}
                    >
                      {pkg.id}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {pkg.rule ? (
                      <Stack direction="row" spacing={0.5} flexWrap="wrap">
                        {pkg.rule.suppliers.map((s) => (
                          <Chip key={s} size="small" label={s} />
                        ))}
                        {pkg.rule.sourceType ? (
                          <Chip
                            size="small"
                            variant="outlined"
                            label={pkg.rule.sourceType}
                          />
                        ) : null}
                        {(pkg.rule.genreId ?? []).length > 0 ? (
                          <Chip
                            size="small"
                            variant="outlined"
                            label={`${pkg.rule.genreId!.length} genre(s)`}
                          />
                        ) : null}
                      </Stack>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        Curated only
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {pkg.rule ? (
                      <Tooltip
                        title={
                          pkg.resolved?.truncated
                            ? "Capped — the package holds at least this many."
                            : ""
                        }
                      >
                        <span>
                          {pkg.resolved?.inert
                            ? "0 — no trusted supplier"
                            : `${pkg.resolved?.ruleCount ?? 0}${pkg.resolved?.truncated ? "+" : ""}`}
                        </span>
                      </Tooltip>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell align="right">{pkg.assets.length}</TableCell>
                  <TableCell align="right">
                    {pkg.excludes.length || "—"}
                  </TableCell>
                  <TableCell align="right">
                    {/* Two actions because they answer two different questions:
                        a rule describes the CATALOGUE ("everything from SVT"),
                        assets state the OFFER ("SVT1, TV4, Kanal 5"). No rule
                        expresses a channel line-up, so the package that every
                        hotel starts from could not be built here at all until
                        this button existed. */}
                    <Button size="small" onClick={() => onPickChannels(pkg)}>
                      Channels
                    </Button>
                    <Button size="small" onClick={() => onEdit(pkg)}>
                      Edit rule
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Card>
  );
}

/* -------------------------------------------------------------------------- */

/**
 * The rule editor, and the reason this whole screen exists.
 *
 * The diff is computed on the server against the REAL catalogue before
 * anything is saved. `pinsOnly` is the number an operator has to look at: those
 * are titles the package holds today that the rule would not carry, and
 * dropping the pinned ids would lose them.
 */
function PackageRuleDialog({
  pkg,
  suppliers,
  onClose,
  onSaved,
}: {
  pkg: ContentPackageRow;
  suppliers: ContentSupplierRow[];
  onClose: () => void;
  onSaved: (overlap: PackageOverlapReport | null) => Promise<void>;
}) {
  // Seeded ONCE. The dialog is remounted by key, so there is nothing to sync.
  const [rule, setRule] = useState<PackageRule>(pkg.rule ?? EMPTY_RULE);
  const [preview, setPreview] = useState<ContentPackagePreview | null>(null);
  // `ENT-3`: what this CANDIDATE rule would make the package a near-copy of.
  // Shown before saving, which is the whole point of §8's safety net.
  const [overlap, setOverlap] = useState<PackageOverlapReport | null>(null);
  const [problems, setProblems] = useState<
    { field: string; message: string }[]
  >([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const trusted = suppliers.filter((s) => s.trusted);

  const runPreview = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await foyerApi.previewPackageRule(
        pkg.id,
        rule.suppliers.length > 0 ? rule : null
      );
      setPreview(res.preview);
      setProblems(res.problems);
      setOverlap(res.overlap);
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setBusy(false);
    }
  };

  const save = async (next: PackageRule | null) => {
    setBusy(true);
    setError(null);
    try {
      const res = await foyerApi.updateContentPackage(pkg.id, { rule: next });
      await onSaved(res.overlap);
    } catch (e) {
      setError(friendlyError(e));
      setBusy(false);
    }
  };

  const field = (
    label: string,
    value: string[] | undefined,
    onChange: (next: string[]) => void,
    helper: string
  ) => (
    <TextField
      label={label}
      size="small"
      fullWidth
      value={(value ?? []).join(", ")}
      onChange={(e) => onChange(parseList(e.target.value))}
      helperText={helper}
    />
  );

  return (
    <Dialog open fullWidth maxWidth="md" onClose={onClose}>
      <DialogTitle>{pkg.name} — rule</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3} sx={{ pt: 1 }}>
          <Alert severity="info">
            A rule must name at least one <strong>trusted</strong> supplier.
            Without that gate it would match anybody’s content, including
            uploads nobody has screened.
          </Alert>

          {trusted.length === 0 ? (
            <Alert severity="warning">
              No supplier is trusted, so no rule can be saved for this package
              yet.
            </Alert>
          ) : null}

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Suppliers
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {trusted.map((supplier) => {
                const on = rule.suppliers.includes(supplier.id);
                return (
                  <Chip
                    key={supplier.id}
                    label={supplier.name}
                    color={on ? "primary" : "default"}
                    variant={on ? "filled" : "outlined"}
                    onClick={() =>
                      setRule((r) => ({
                        ...r,
                        suppliers: on
                          ? r.suppliers.filter((id) => id !== supplier.id)
                          : [...r.suppliers, supplier.id],
                      }))
                    }
                  />
                );
              })}
            </Stack>
          </Box>

          <Typography variant="subtitle2">
            Narrow it (optional — everything below is ANDed)
          </Typography>
          <Stack spacing={2}>
            <TextField
              label="Source type"
              size="small"
              select
              SelectProps={{ native: true }}
              value={rule.sourceType ?? ""}
              onChange={(e) =>
                setRule((r) => ({
                  ...r,
                  sourceType: (e.target.value ||
                    undefined) as PackageRule["sourceType"],
                }))
              }
              sx={{ maxWidth: 220 }}
            >
              <option value="">Any</option>
              <option value="vod">vod</option>
              <option value="live">live</option>
              <option value="catchup">catchup</option>
              <option value="deeplink">deeplink</option>
            </TextField>
            {field(
              "Genre ids",
              rule.genreId,
              (genreId) => setRule((r) => ({ ...r, genreId })),
              "Comma-separated. Matches any of them."
            )}
            {field(
              "Tag ids",
              rule.tagId,
              (tagId) => setRule((r) => ({ ...r, tagId })),
              "Comma-separated. Matches any of them."
            )}
            {field(
              "Keywords",
              rule.keyword,
              (keyword) => setRule((r) => ({ ...r, keyword })),
              "Exact matches, not substrings — two spellings are two keywords."
            )}
          </Stack>

          {problems.length > 0 ? (
            <Alert severity="warning">
              <Stack spacing={0.5}>
                {problems.map((p) => (
                  <span key={`${p.field}:${p.message}`}>{p.message}</span>
                ))}
              </Stack>
            </Alert>
          ) : null}

          {error ? <Alert severity="error">{error}</Alert> : null}

          <PackageOverlapNotice
            report={overlap}
            onDismiss={() => setOverlap(null)}
          />

          {preview ? (
            <Card variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                What this rule would do
              </Typography>
              <Stack direction="row" spacing={3} sx={{ mb: 1 }}>
                <Stat
                  label="Titles the rule yields"
                  value={`${preview.ruleCount}${preview.truncated ? "+" : ""}`}
                />
                <Stat label="It adds" value={preview.ruleOnly.length} />
                <Stat
                  label="Curated titles it already covers"
                  value={preview.covered.length}
                />
                <Stat
                  label="Curated titles it does NOT cover"
                  value={preview.pinsOnly.length}
                  warn={preview.pinsOnly.length > 0}
                />
              </Stack>
              {preview.pinsOnly.length > 0 ? (
                <Alert severity="warning">
                  {preview.pinsOnly.length} pinned title(s) would not be carried
                  by this rule. They stay in the package as curated titles —
                  dropping them later would lose them. Some may be ids of
                  content that no longer exists.
                </Alert>
              ) : null}
              {preview.truncated ? (
                <Alert severity="info" sx={{ mt: 1 }}>
                  The count is capped, so the package holds at least this many.
                  Playback is unaffected: the entitlement check never uses this
                  list.
                </Alert>
              ) : null}
            </Card>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        {pkg.rule ? (
          <Button
            color="warning"
            onClick={() => void save(null)}
            disabled={busy}
          >
            Remove rule (keep curated titles)
          </Button>
        ) : null}
        <Button onClick={() => void runPreview()} disabled={busy}>
          Preview
        </Button>
        <Button
          variant="contained"
          disabled={busy || rule.suppliers.length === 0}
          onClick={() => void save(rule)}
        >
          Save rule
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function Stat({
  label,
  value,
  warn,
}: {
  label: string;
  value: string | number;
  warn?: boolean;
}) {
  return (
    <Box>
      <Typography
        variant="h6"
        sx={{ color: warn ? "warning.main" : "text.primary" }}
      >
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );
}

/* -------------------------------------------------------------------------- */

// Slug + id rule are shared with the product dialog: same permanence, same
// Swedish-diacritic trap.
const slugifyPackageId = slugifyId;
const PACKAGE_ID_RE = CATALOGUE_ID_RE;

/**
 * New package.
 *
 * ## Why the name is asked first
 *
 * This dialog used to ask for the ID first, then the name — the hardest and
 * most irreversible decision presented before the easy one, to somebody who had
 * decided neither and had not yet picked a single channel.
 *
 * The id is the `_id`. Mongo will not change it, `Product.grants.packages`,
 * `FeatureSet.lineItems` and `Room.entitlement.packages` all point at it, and
 * there is no rename — only delete-and-recreate, which orphans every reference.
 * So it is permanent in the strong sense, and asking for it cold was asking for
 * a guess that lasts forever.
 *
 * Name first, id derived, id still shown and still editable. The derived value
 * stops following the name the moment somebody edits it by hand — the standard
 * slug-field behaviour, and the thing that makes overriding feel safe.
 *
 * ## Why the id is visible at all
 *
 * A generated-and-hidden id would remove this whole question. It is shown
 * because a human-readable id is worth a great deal when something is wrong at
 * eleven at night: the string in the log, the string in the package list and
 * the string in a support message are the same string. `basic` beats `66f3a…`
 * in every one of those places.
 */
function CreatePackageDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => Promise<void>;
}) {
  const [name, setName] = useState("");
  const [id, setId] = useState("");
  /** True once the id has been typed into: stop following the name. */
  const [idTouched, setIdTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const effectiveId = idTouched ? id : slugifyPackageId(name);
  const idValid = effectiveId === "" || PACKAGE_ID_RE.test(effectiveId);

  const create = async () => {
    setBusy(true);
    setError(null);
    try {
      await foyerApi.createContentPackage({
        id: effectiveId,
        name: name.trim(),
      });
      await onCreated();
    } catch (e) {
      setError(friendlyError(e));
      setBusy(false);
    }
  };

  return (
    <Dialog open fullWidth maxWidth="sm" onClose={busy ? undefined : onClose}>
      <DialogTitle>New package</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Alert severity="info">
            Name the <strong>content</strong>, never the customer:{" "}
            <code>Basic</code>, <code>Sports</code>, <code>Signage loop</code>.
            One package per distinct set of content — who gets it, and at what
            price, is decided on a contract rather than by copying the package.
          </Alert>

          <TextField
            label="Name"
            size="small"
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Swedish basic"
            inputProps={{ "data-testid": "pkg-new-name" }}
          />

          <TextField
            label="Id"
            size="small"
            value={effectiveId}
            onChange={(e) => {
              setIdTouched(true);
              setId(e.target.value);
            }}
            error={!idValid}
            placeholder="swedish-basic"
            inputProps={{ "data-testid": "pkg-new-id" }}
            helperText={
              !idValid
                ? "Lower-case letters, digits and hyphens, starting with a letter or digit."
                : idTouched
                  ? "Permanent — it cannot be renamed afterwards."
                  : "Derived from the name. Edit it if you want something shorter — it is permanent afterwards."
            }
            sx={{ "& input": { fontFamily: "ui-monospace, Menlo, monospace" } }}
          />

          {error ? <Alert severity="error">{error}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => void create()}
          disabled={busy || !effectiveId || !idValid}
          data-testid="pkg-new-create"
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
}
